// src/components/PremiumUpgradeModal.tsx
import React, { useState, useEffect } from 'react';
import { AI_FEATURES_SUSPENDED, AI_SUSPENDED_BADGE } from '@/config/aiStatus';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useSnackbar } from 'notistack';
import { useAuth } from '@/contexts/AuthProvider';
import { onSnapshot } from 'firebase/firestore';
import { useFirestore } from '@/hooks/useFirestore';
import { Capacitor } from '@capacitor/core';
import { initializePurchases, purchasePackage } from '@/services/nativePurchaseService';
import { authedPost } from '@/services/apiClient';
import { GoldSpinner } from '@/components/motion';
import { colors } from '@/theme/tokens';
import { trackGA4 } from '@/utils/analytics';
import { recordFunnel, recordFunnelBeforeNavigation } from '@/utils/funnel';

// StripeプライスID
// 2026-09-29: 購入画面(/account)と**別の価格**を使っていた。
// ここは product「中小企業診断士試験アプリ Premium会員」(prod_TMrIv2Mn9F9Tvg)、
// 購入画面は「診断士RoadMap Premium」(prod_TNcfihXWjgGe4D)。**同じ¥500だが別商品**で、
// 週次レポートと棚卸しは後者の price だけを数える＝
// **このモーダル経由で課金した人は「有料会員0」と報告され続ける**状態だった。
// 現行の1つに寄せる。旧 price は参照されなくなるので Stripe 側で archive する。
const STRIPE_PRICE_ID_LIVE = 'price_1SQrQOJm6nYxRNS1wU8ldhBN';
const STRIPE_PRICE_ID_TEST = 'price_1SQ7aOJm6nYxRNS1bNPnl6Sv';

// Premium設定の定数
// 【2026-09-28 修正】ここの数字は**購入直前の画面に出る訴求**なので、実装と一致させる。
// `PREMIUM_DAILY_AI_CHAT_LIMIT` は 100 になっていたが、**表示専用でどこからも強制されておらず**、
// 実際に効いているのは functions/handlers/aiHandler.ts の
// `DAILY_CAP_PREMIUM = 200` / `DAILY_CAP_FREE = 20`（サーバ側で原子的に数えて拒否する）。
// **サーバの値が正。** 変えるときは aiHandler と一緒に直すこと。
export const PREMIUM_CONFIG = {
  FREE_AI_QUESTIONS: 3, // 無料で見られるAI問題数（AIQuestionChat/SubjectYear で実際に制限）
  FREE_AI_CHAT_PER_QUESTION: 1, // 1問につき無料のAIチャット回数（AIQuestionChat で実際に制限）
  PREMIUM_DAILY_AI_CHAT_LIMIT: 200, // = aiHandler の DAILY_CAP_PREMIUM
  FREE_DAILY_AI_CHAT_LIMIT: 20, // = aiHandler の DAILY_CAP_FREE（無料でも1日の上限がある）
  // 二次試験のAI添削。**数字を直書きしない**（実装とズレても誰も気づかないため）
  PREMIUM_DAILY_GRADE_LIMIT: 100, // = gradingHandler の GRADE_CAP_PREMIUM
  FREE_DAILY_GRADE_LIMIT: 10, // = gradingHandler の GRADE_CAP_FREE
};

interface PremiumUpgradeModalProps {
  open: boolean;
  onClose: () => void;
  /** ロック理由: 'ai_question' | 'ai_chat' */
  lockReason?: 'ai_question' | 'ai_chat';
}

const PremiumUpgradeModal: React.FC<PremiumUpgradeModalProps> = ({
  open,
  onClose,
  lockReason = 'ai_question',
}) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const firestore = useFirestore();
  const [loading, setLoading] = useState(false);
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState('');

  // ペイウォール(アップグレードモーダル)表示の計測
  useEffect(() => {
    if (open) {
      void recordFunnel('paywall_view', user?.uid, { source: `modal_${lockReason}` });
    }
  }, [open, lockReason, user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;

    const unsub = onSnapshot(firestore.getUserDoc(user.uid), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setStripeSubscriptionId(data.stripeSubscriptionId || '');
    });

    return () => unsub();
  }, [user, firestore]);

  const handleSubscribe = async () => {
    // 決済導線は遷移を伴うため、記録の完了を待ってから進む（上限付き）
    await recordFunnelBeforeNavigation('checkout_start', user?.uid, {
      source: `modal_${lockReason}`,
      platform: Capacitor.isNativePlatform() ? 'native' : 'web',
    });
    if (!user) {
      enqueueSnackbar('ログインが必要です', { variant: 'warning' });
      return;
    }

    // ネイティブ (iOS/Android) は App Store / Google Play の内課金 (RevenueCat) を使用
    if (Capacitor.isNativePlatform()) {
      setLoading(true);
      try {
        await initializePurchases(user.uid);
        const hasPremium = await purchasePackage('$rc_monthly');
        if (hasPremium) {
          enqueueSnackbar('Premiumへのアップグレードが完了しました！', {
            variant: 'success',
          });
          onClose();
        }
      } catch (e: any) {
        if (e?.code !== 'PURCHASE_CANCELLED') {
          enqueueSnackbar(`購入エラー: ${e.message}`, { variant: 'error' });
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    if (stripeSubscriptionId) {
      enqueueSnackbar('すでにサブスクリプションが登録されています。', {
        variant: 'info',
      });
      return;
    }

    setLoading(true);

    try {
      const response = await authedPost('/stripe/create-checkout-session', {
        priceId: STRIPE_PRICE_ID_LIVE,
        userId: user.uid,
        email: user.email || undefined,
        successUrl: `${window.location.origin}/?stripe_success=true&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/?stripe_cancelled=true`,
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || 'Unknown error');
      }

      if (json.url) {
        window.location.href = json.url;
      }
    } catch (e: any) {
      enqueueSnackbar(`エラー: ${e.message}`, { variant: 'error' });
      setLoading(false);
    }
  };

  // プラン比較データ。
  // **実装で確認できた差分だけを書く**（2026-09-24 監査）。
  // 以前は「二次試験AI添削: 無料=×」「優先サポート」と表示していたが、
  // 前者は無料でも1日10回使え、後者は実装が存在しなかった。
  const comparisonData = [
    {
      feature: 'AI問題',
      free: `${PREMIUM_CONFIG.FREE_AI_QUESTIONS}問まで`,
      premium: '無制限',
      icon: <SmartToyIcon sx={{ color: colors.gold }} />,
    },
    {
      // 【2026-09-29】AI の鍵が全て無効で AIチャットは動いていない。
      // **動かない機能の上限を売り文句にしない**ため、復旧まで回数を出さず停止中と書く。
      feature: `AIチャット（質問）${AI_FEATURES_SUSPENDED ? AI_SUSPENDED_BADGE : ''}`,
      free: AI_FEATURES_SUSPENDED
        ? '—'
        : `1問につき${PREMIUM_CONFIG.FREE_AI_CHAT_PER_QUESTION}回・1日${PREMIUM_CONFIG.FREE_DAILY_AI_CHAT_LIMIT}回まで`,
      premium: AI_FEATURES_SUSPENDED ? '復旧までお待ちください' : `1日${PREMIUM_CONFIG.PREMIUM_DAILY_AI_CHAT_LIMIT}回まで`,
      icon: <SmartToyIcon sx={{ color: colors.info }} />,
    },
    {
      // 【2026-09-29】こちらも同じ鍵を使っており動いていない（フォールバック無し）
      // 2026-10-02: 自分の答案を貼って採点する機能が live になったので注記を外した。
      feature: `二次試験のAI添削（自分の答案を貼れます）${AI_FEATURES_SUSPENDED ? AI_SUSPENDED_BADGE : ''}`,
      free: AI_FEATURES_SUSPENDED ? '—' : `1日${PREMIUM_CONFIG.FREE_DAILY_GRADE_LIMIT}回まで`,
      premium: AI_FEATURES_SUSPENDED
        ? '復旧までお待ちください'
        : `1日${PREMIUM_CONFIG.PREMIUM_DAILY_GRADE_LIMIT}回まで`,
    },
    {
      feature: '体感型解説の演習・応用',
      free: '冒頭のみ',
      premium: 'すべて',
    },
    {
      feature: '過去問解説・学習履歴・ブックマーク',
      free: true,
      premium: true,
    },
    {
      feature: '論点解説202本・ミニ模試・コミュニティ',
      free: true,
      premium: true,
    },
  ];

  const renderValue = (value: boolean | string, isPremiumColumn?: boolean) => {
    if (typeof value === 'string') {
      // Premiumカラムの「無制限」や「1日100回まで」は金色で目立たせる
      const isGolden = isPremiumColumn && (value.includes('無制限') || value.includes('回まで'));
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: isGolden ? 700 : 500,
            color: isGolden ? colors.gold : undefined,
          }}
        >
          {value}
        </Typography>
      );
    }
    return value ? (
      <CheckCircleIcon sx={{ color: colors.success }} />
    ) : (
      <CancelIcon sx={{ color: 'text.disabled' }} />
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '700px' },
          maxHeight: '90vh',
          bgcolor: colors.surface,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          overflow: 'hidden',
          border: `1px solid ${colors.borderNeutral}`,
        }}
      >
        {/* ヘッダー */}
        <Box
          sx={{
            bgcolor: colors.surfaceHigh,
            color: colors.textPrimary,
            p: 3,
            position: 'relative',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: colors.textSecondary,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WorkspacePremiumIcon sx={{ fontSize: 40, color: colors.gold }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {lockReason === 'ai_question'
                  ? 'AI問題をもっと解こう！'
                  : 'AIにもっと質問しよう！'}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Premiumにアップグレードして全機能を解放
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* コンテンツ */}
        <Box sx={{ p: 3, maxHeight: 'calc(90vh - 200px)', overflowY: 'auto', bgcolor: colors.surface }}>
          {/* ロック理由の説明 */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              bgcolor: lockReason === 'ai_question' ? colors.goldSoft : colors.surfaceHigh,
              border: `1px solid ${lockReason === 'ai_question' ? colors.border : colors.info}`,
            }}
          >
            <Typography variant="body1" sx={{ color: colors.textPrimary }}>
              {lockReason === 'ai_question' ? (
                <>
                  無料プランでは、各問題につき
                  <strong style={{ color: colors.gold }}>AI問題{PREMIUM_CONFIG.FREE_AI_QUESTIONS}問まで</strong>
                  ご利用いただけます。
                  <br />
                  Premiumにアップグレードすると、すべてのAI問題を解くことができます。
                </>
              ) : (
                <>
                  無料プランでは、各問題につき
                  <strong style={{ color: colors.info }}>AIチャット{PREMIUM_CONFIG.FREE_AI_CHAT_PER_QUESTION}回まで</strong>
                  ご利用いただけます。
                  <br />
                  Premiumにアップグレードすると、1日{PREMIUM_CONFIG.PREMIUM_DAILY_AI_CHAT_LIMIT}回までAIに質問できます。
                </>
              )}
            </Typography>
          </Paper>

          {/* プラン比較表 */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: colors.textPrimary }}>
            プラン比較
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 3, bgcolor: colors.surfaceHigh, boxShadow: 'none', border: `1px solid ${colors.borderNeutral}` }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: colors.surfaceHigher }}>
                  <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary, borderBottom: `1px solid ${colors.borderNeutral}` }}>機能</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 140, color: colors.textSecondary, borderBottom: `1px solid ${colors.borderNeutral}` }}>
                    無料プラン
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      width: 140,
                      bgcolor: colors.goldSoft,
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: colors.gold }}>
                      <WorkspacePremiumIcon sx={{ fontSize: 18, color: colors.gold }} />
                      Premium
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: colors.surface },
                      '&:nth-of-type(even)': { bgcolor: colors.surfaceHigh },
                    }}
                  >
                    <TableCell sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.borderNeutral}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {row.icon}
                        {row.feature}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.borderNeutral}` }}>
                      {renderValue(row.free, false)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ bgcolor: colors.goldSoft, borderBottom: `1px solid ${colors.border}` }}
                    >
                      {renderValue(row.premium, true)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 料金 */}
          <Box
            sx={{
              py: 2.5,
              mb: 3,
              textAlign: 'center',
              bgcolor: colors.surface,
              borderTop: `1px solid ${colors.border}`,
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              月額
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mt: 0.25,
                fontWeight: 'bold',
                color: colors.textPrimary,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              ¥500
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
              いつでもキャンセル可能・初月から適用
            </Typography>
          </Box>

          <Divider sx={{ my: 2, borderColor: colors.borderNeutral }} />

          {/* 登録ボタン */}
          <Box sx={{ textAlign: 'center' }}>
            {loading ? (
              <GoldSpinner />
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={handleSubscribe}
                startIcon={<WorkspacePremiumIcon />}
                sx={{
                  px: 5,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                }}
              >
                Premiumにアップグレード
              </Button>
            )}

            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 2, color: 'text.secondary' }}
            >
              {Capacitor.isNativePlatform()
                ? 'App Store / Google Play による安全な決済'
                : 'Stripeによる安全な決済・クレジットカード対応'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PremiumUpgradeModal;
